import { Editor } from 'slate';
import { Button, icons, Modal, TextField } from '@ublend-npm/aulaui-next';
import { ReactEditor, useSlateStatic } from 'slate-react';
import React, { useState } from 'react';
import { insertAndUpload } from '../../lib/insertAndUpload';
import {
  ActionButtons,
  AudioPlayerContainer,
  AudioRecorderContainer,
  Title,
  Container,
} from './AudioModal.styled';
import { AulaEditorContext } from '../../types';
import { AudioPlayer } from '../AudioPlayer/AudioPlayer';
import { AudioPlayerButton } from '../AudioPlayer/AudioPlayer.styled';
import { useMediaRecorder } from '../../lib/useMediaRecorder';
import { useAnalytics } from '../../lib/useAnalytics';

export type AudioModalProps = {
  context: Omit<AulaEditorContext, 'entities'>;
};

/**
 * Audio Modal Component
 */
export const AudioModal: React.FunctionComponent<AudioModalProps> = ({
  context,
}) => {
  const { recordingPath, setRecordingPath } = context;
  const track = useAnalytics();

  const {
    startRecording,
    blob,
    timeRecorded,
    stopRecording,
    fileUrl,
    isRecording,
  } = useMediaRecorder();

  const [fileName, setFileName] = useState<string | null>(null);
  const editor = useSlateStatic();

  return (
    <Modal open keepMounted size="medium" id="audio-modal">
      <Container data-testid="audio-modal">
        <Title>Voice recording</Title>
        {fileUrl == null && (
          <AudioRecorderContainer>
            <AudioPlayerButton
              aria-label={`${isRecording ? 'Stop' : 'Start'} recording`}
              onClick={async () => {
                if (isRecording) {
                  track('START_VOICE_RECORDING');
                  await stopRecording();
                  track('STOP_VOICE_RECORDING');
                } else {
                  await startRecording();
                }
              }}
            >
              {isRecording ? (
                <icons.Stop height={12} />
              ) : (
                <icons.Mic height={19} />
              )}
            </AudioPlayerButton>
            <time data-testid="time-recorded">{timeRecorded}</time>
          </AudioRecorderContainer>
        )}
        {fileUrl != null && (
          <AudioPlayerContainer>
            <AudioPlayer src={fileUrl} />
          </AudioPlayerContainer>
        )}
        <TextField
          label="Recording filename"
          type="text"
          id="audio-filename-input"
          onChange={(e: any) => {
            setFileName(e.target.value);
          }}
        />
        <ActionButtons>
          <Button
            onClick={async () => {
              track('DISCARD_VOICE_RECORDING');
              await stopRecording();
              setRecordingPath(null);
              setTimeout(() => {
                ReactEditor.focus(editor);
              }, 0);
            }}
          >
            Discard
          </Button>
          <Button
            type="primary"
            disabled={
              fileName == null ||
              (fileName != null && fileName.length < 3) ||
              (!isRecording && !blob)
            }
            onClick={async () => {
              if (fileName != null) {
                const val = blob || (await stopRecording());

                const file = new File([val], `${fileName}.wav`, {
                  type: 'audio/wav',
                });

                track('UPLOAD_VOICE_RECORDING');

                insertAndUpload({
                  file,
                  editor,
                  range: Editor.range(editor, recordingPath),
                  context,
                  isRecording: true,
                });

                setRecordingPath(null);

                setTimeout(() => {
                  ReactEditor.focus(editor);
                }, 0);
              }
            }}
          >
            Done
          </Button>
        </ActionButtons>
      </Container>
    </Modal>
  );
};
